import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import NotFound from '../../../pages/NotFound';
import { clearState, getState } from '../../../store/reducers/app';
import { selectPills } from '../../../store/reducers/search';
import { getUser } from '../../../store/reducers/user';
import { trackPageView } from '../../../utils/mixpanel/mixpanelEvents';
import { isEmptyObject } from '../../../utils/object';
import Loader from '../../base/Loader';
import { showErrorMessage } from '../../base/Notifications';
import ForbiddenModal from '../../ui/modals/ForbiddenModal';
import LoginModal from '../../ui/modals/LoginModal';
import Footer from '../Footer';
import Header from '../Header';
import './Wrapper.scss';

const ErrorFallback = ({ error }) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`Error : ${error.message}`);
  } else {
    console.error(`Error : ${error.message}`);
  }

  const state = useSelector(getState);

  if (!state.isError) {
    showErrorMessage(
      'Something went wrong. Please, refresh the page and try again.'
    );
  }

  return null;
};

const Wrapper = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const { isAuthenticated, isLoading, logout } = useAuth0();
  const activePills = useSelector(selectPills);
  const state = useSelector(getState);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  const onProceed = () => {
    if (location.pathname.startsWith('/form-analytics/')) {
      dispatch(clearState());
    } else if (location.pathname !== '/dashboard') {
      navigate(-1);
      dispatch(clearState());
    } else {
      dispatch(clearState());
      logout();
    }
  };

  useEffect(() => {
    if (
      import.meta.env.VITE_APP_MIXPANEL_TOKEN &&
      Object.keys(user).length > 0
    ) {
      trackPageView(user, location.pathname);
    }
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoadingUser(false), 1000);
    return () => clearTimeout(timer);
  }, [user.data_export]);

  if (state.isError && state.errorType === 'Not Found') {
    return <NotFound />;
  }

  if (isLoadingUser) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <div
      className="wrapper d-flex flex-column"
      onClick={() => {
        setShowDropdown(false);
      }}
    >
      <header>
        <Header showDropdown={showDropdown} setShowDropdown={setShowDropdown} />
      </header>

      <main className="container-fluid mb-3 main">
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          {isAuthenticated ||
          ['/accepted', '/rejected', '/gcp-signup'].some((path) =>
            location.pathname.startsWith(path)
          ) ? (
            <Outlet />
          ) : isLoading ? (
            <div className="p-5">
              <Loader />
            </div>
          ) : (
            <LoginModal />
          )}
        </ErrorBoundary>
      </main>
      <footer className="mt-auto">
        {activePills.length ? <Footer type="homepage" /> : null}
      </footer>
      {state.isError && state.errorType === 'Unauthorized' && <LoginModal />}
      {((state.isError && state.errorType === 'Rejected') ||
        (!isLoadingUser &&
          !isEmptyObject(user) &&
          !user.data_export &&
          location.pathname.startsWith('/csv-upload')) ||
        (!isLoadingUser &&
          !isEmptyObject(user) &&
          !user.forms &&
          location.pathname.startsWith('/add-form')) ||
        (!isLoadingUser &&
          !isEmptyObject(user) &&
          !user.web_campaign &&
          location.pathname.startsWith('/form/campaign')) ||
        (!isLoadingUser &&
          !isEmptyObject(user) &&
          !user.contacts &&
          location.pathname.startsWith('/contacts'))) && (
        <ForbiddenModal
          onProceed={onProceed}
          toLogin={location.pathname === '/dashboard'}
        />
      )}
    </div>
  );
};

export default Wrapper;
